import { cn } from "@pax/ui";
import { type CSSProperties, useEffect, useId, useRef } from "react";

/**
 * The world behind the sign-in screen: a wire globe, turning.
 *
 * It is the same graticule the Pax mark is drawn from, at the size of the
 * screen rather than of a favicon, and it is the only thing on the page that
 * is not a word or a button. How it moves is in `.turning-world` in
 * `src/styles.css`.
 */

/** The sphere, in the units of its own square. The square is 200 across. */
const RADIUS = 96;

/** How far the globe is tipped towards the reader, in degrees. */
const TILT = 18;

/** Great circles through the poles, and the rings stacked between them. */
const MERIDIAN_COUNT = 6;
const PARALLELS_PER_HEMISPHERE = 3;

const radians = (degrees: number) => (degrees * Math.PI) / 180;

/** How much of the sphere's height survives the tilt. */
const SQUASH = Math.cos(radians(TILT));

/**
 * A ring of latitude: as wide as the sphere is at that height, flattened by
 * the tilt, and sitting at the height the tilt leaves it at.
 */
const parallels = Array.from({ length: PARALLELS_PER_HEMISPHERE * 2 + 1 }, (_, index) => {
  const latitude = (index - PARALLELS_PER_HEMISPHERE) * (90 / (PARALLELS_PER_HEMISPHERE + 1));
  const width = RADIUS * Math.cos(radians(latitude));
  return {
    id: latitude,
    cy: -RADIUS * Math.sin(radians(latitude)) * SQUASH,
    rx: width,
    ry: width * Math.sin(radians(TILT)),
  };
});

/**
 * A meridian is an ellipse as tall as the tilted sphere, and as wide as the
 * cosine of how far round it has turned. Turning the globe is therefore one
 * `scaleX` per meridian, which the compositor can do on its own.
 *
 * Each one starts a fixed fraction of a turn behind the last. A meridian and
 * the one opposite it are the same great circle, so the whole pattern repeats
 * every half turn: the fractions are spread over that half rather than over
 * the whole.
 */
const meridians = Array.from({ length: MERIDIAN_COUNT }, (_, index) => {
  const turned = (index / MERIDIAN_COUNT) * 180;
  return {
    id: turned,
    style: {
      transform: `scaleX(${Math.cos(radians(turned)).toFixed(4)})`,
      animationDelay: `calc(var(--world-turn) * ${(-index / (MERIDIAN_COUNT * 2)).toFixed(4)})`,
    } as CSSProperties,
  };
});

/** How tall the band of stronger ink is, trailing behind the scan line. */
const SCAN_BAND = 60;

/** How long a scan takes to finish sweeping once its check lands. */
export const SCAN_COMPLETE_MS = 600;

/** How long the scan creeps for while it waits, after which it holds where it is. */
const SCAN_CREEP_MS = 30_000;

/**
 * How far down the globe the scan line has crept `ms` into a check, from 0 at the top to 1 at the
 * bottom: quick at first, then slower and slower, never quite reaching the bottom.
 */
function scanCrept(ms: number): number {
  return 0.9 * (1 - Math.exp(-ms / 2_000));
}

/** Where the line and band are `crept` of the way down, as a transform. */
function scanAt(crept: number): Keyframe {
  return { transform: `translateY(${(crept * 2 * RADIUS).toFixed(2)}px)` };
}

/** Past the bottom, with the band clear of the globe. */
const SCAN_DONE = { transform: `translateY(${2 * RADIUS + SCAN_BAND}px)` };

const creepFrames = Array.from({ length: 31 }, (_, second) => scanAt(scanCrept(second * 1_000)));

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function Graticule() {
  return (
    <>
      <circle r={RADIUS} />
      {parallels.map(parallel => (
        <ellipse key={parallel.id} cy={parallel.cy} rx={parallel.rx} ry={parallel.ry} />
      ))}
      {meridians.map(meridian => (
        <ellipse
          key={meridian.id}
          className="meridian"
          rx={RADIUS}
          ry={RADIUS * SQUASH}
          style={meridian.style}
        />
      ))}
    </>
  );
}

/**
 * The globe. While `scanning`, the same globe in stronger ink shows inside a band that sweeps
 * down it, which is how a session is checked on the globe the person was already looking at. The
 * sweep starts at the top and creeps like a progress bar that's waiting; once `complete`, it
 * finishes sweeping in `SCAN_COMPLETE_MS`.
 */
export function TurningWorld({
  scanning = false,
  complete = false,
  className,
}: {
  scanning?: boolean;
  complete?: boolean;
  className?: string;
}) {
  const id = useId();
  const band = useRef<SVGRectElement>(null);
  const line = useRef<SVGLineElement>(null);
  const sweeps = useRef<Animation[]>([]);

  useEffect(() => {
    if (!scanning || prefersReducedMotion()) return;
    const targets = [band.current, line.current].filter(target => target !== null);
    sweeps.current = targets.map(target =>
      target.animate(creepFrames, { duration: SCAN_CREEP_MS, fill: "forwards" }),
    );
    return () => {
      for (const sweep of sweeps.current) sweep.cancel();
      sweeps.current = [];
    };
  }, [scanning]);

  useEffect(() => {
    if (!complete) return;
    sweeps.current = sweeps.current.map(creep => {
      const crept = scanCrept(Math.min(Number(creep.currentTime ?? 0), SCAN_CREEP_MS));
      const target = (creep.effect as KeyframeEffect).target as Element;
      creep.cancel();
      return target.animate([scanAt(crept), SCAN_DONE], {
        duration: SCAN_COMPLETE_MS,
        easing: "cubic-bezier(0.4, 0, 0.2, 1)",
        fill: "forwards",
      });
    });
  }, [complete]);

  return (
    <svg
      aria-hidden="true"
      viewBox="-100 -100 200 200"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      data-scanning={scanning}
      // Wider than a phone on purpose: a globe that fits the window reads as
      // a diagram, and one that runs off it reads as a world.
      className={cn(
        "turning-world pointer-events-none absolute -z-10 aspect-square h-[min(34rem,130vw,88vh)] text-foreground/[0.07] dark:text-foreground/[0.11]",
        className,
      )}
    >
      <defs>
        <clipPath id={`${id}-band`}>
          <rect ref={band} x={-110} y={-RADIUS - SCAN_BAND} width={220} height={SCAN_BAND} />
        </clipPath>
        <clipPath id={`${id}-sphere`}>
          <circle r={RADIUS} />
        </clipPath>
      </defs>
      <Graticule />
      <g
        className="scanned text-foreground/30 dark:text-foreground/35"
        clipPath={`url(#${id}-band)`}
      >
        <Graticule />
      </g>
      <g
        className="scanned text-foreground/30 dark:text-foreground/35"
        clipPath={`url(#${id}-sphere)`}
      >
        <line ref={line} x1={-110} x2={110} y1={-RADIUS} y2={-RADIUS} />
      </g>
    </svg>
  );
}
